import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Search, Sun, Moon } from "lucide-react";
import Logo from "./Logo";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/cars", label: "Buy a Car" },
  { to: "/latest-arrivals", label: "Latest Arrivals" },
  { to: "/sell-your-car", label: "Sell Your Car" },
  { to: "/about", label: "About" },
  { to: "/reviews", label: "Reviews" },
  { to: "/contact", label: "Contact" },
];

export const Header = ({ theme, toggleTheme }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled ? "bg-[#F7F6F3]/90 backdrop-blur-xl border-b hairline" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="container-wide flex items-center justify-between h-20">
        <Link to="/" className="flex items-center" data-testid="header-logo-link">
          <Logo className="h-11 w-11" showWordmark />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) =>
                `text-[15px] font-medium tracking-tight transition-colors ${
                  isActive ? "text-[#7A1519]" : "text-[#242424] hover:text-[#7A1519]"
                }`
              }
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/cars"
            className="hidden lg:flex items-center gap-2 text-sm text-[#242424] hover:text-[#7A1519] transition-colors"
            data-testid="header-search"
          >
            <Search size={16} />
            <span>Search</span>
          </Link>
          {toggleTheme && (
            <button
              onClick={toggleTheme}
              className="p-2 text-[#242424] hover:text-[#7A1519] transition-colors rounded-sm"
              aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
              title={theme === "dark" ? "Light mode" : "Dark mode"}
              data-testid="theme-toggle"
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          )}
          <Link
            to="/cars"
            className="hidden md:inline-flex bg-[#7A1519] text-white text-sm px-5 py-2.5 rounded-sm hover:bg-[#5a0f12] transition-colors font-medium"
            data-testid="header-cta-explore"
          >
            Explore Cars
          </Link>
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-[#111111]"
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#F7F6F3] border-t hairline" data-testid="mobile-menu">
          <div className="container-wide py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `text-base font-medium py-2 border-b hairline ${
                    isActive ? "text-[#7A1519]" : "text-[#242424]"
                  }`
                }
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/cars"
              className="mt-2 bg-[#7A1519] text-white text-sm px-5 py-3 rounded-sm text-center"
              data-testid="mobile-cta-explore"
            >
              Explore Cars
            </Link>
            {toggleTheme && (
              <button
                onClick={toggleTheme}
                className="mt-1 inline-flex items-center justify-center gap-2 border hairline text-sm text-[#242424] px-5 py-3 rounded-sm"
                data-testid="mobile-theme-toggle"
              >
                {theme === "dark" ? <><Sun size={16} /> Light mode</> : <><Moon size={16} /> Dark mode</>}
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
